import styled from 'styled-components';

import {
  RegistrationCompleteActions,
  RegistrationCompleteHeading,
  RegistrationCompleteProfilePreview,
} from '../features/registration/ui';
import { nonSelectableSurfaceStyles } from '../shared/ui';

const FOOTER_GAP_PX = 14;
const FOOTER_BOTTOM_PADDING_PX = 8;
const FOOTER_SECONDARY_MIN_HEIGHT_PX = 36;
const FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX = 44;

type RegistrationCompletePageContentProps = {
  hasUploadedAvatar?: boolean;
  isContinuing?: boolean;
  state: 'congrats' | 'card';
  onContinue: () => void;
  submitError?: string | null;
};

const registrationCompleteScreenDataUiByState: Record<
  RegistrationCompletePageContentProps['state'],
  string
> = {
  card: 'registration-complete-card-screen',
  congrats: 'registration-complete-congrats-screen',
};

const Screen = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${nonSelectableSurfaceStyles};

  & > [data-ui='registration-complete-heading'] {
    margin-left: ${({ theme }) => theme.scale.px(31)};
    margin-bottom: ${({ theme }) => theme.scale.px(25)};
  }

  & > [data-ui='registration-complete-profile-preview'] {
    align-self: center;
    margin-bottom: auto;
  }
`;

const SubmitError = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
`;

const Footer = styled.div.attrs({
  'data-ui': 'registration-complete-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(FOOTER_GAP_PX)};
  padding-bottom: ${({ theme }) => theme.scale.px(FOOTER_BOTTOM_PADDING_PX)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'registration-complete-footer-secondary',
})<{ $minHeightPx: number }>`
  width: 100%;
  min-height: ${({ $minHeightPx, theme }) => theme.scale.px($minHeightPx)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

export function RegistrationCompletePageContent({
  hasUploadedAvatar = false,
  isContinuing = false,
  state,
  onContinue,
  submitError = null,
}: RegistrationCompletePageContentProps) {
  const previewVariant = hasUploadedAvatar ? 'with-photo' : 'without-photo';

  return (
    <Screen data-ui={registrationCompleteScreenDataUiByState[state]}>
      <RegistrationCompleteHeading state={state} />
      <RegistrationCompleteProfilePreview previewVariant={previewVariant} />
      <Footer>
        <RegistrationCompleteActions
          isDisabled={isContinuing}
          onContinue={onContinue}
        />
        <FooterSecondary
          $minHeightPx={
            submitError
              ? FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX
              : FOOTER_SECONDARY_MIN_HEIGHT_PX
          }
        >
          {submitError ? <SubmitError>{submitError}</SubmitError> : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}
